/* eslint-disable react/no-danger */
import { GetServerSideProps } from 'next';
import AppContainer from '~/containers/AppContainer/AppContainer';
import { Container } from '~/vinovest-design-system/components';
import React, { useEffect, useRef } from 'react';
import styles from './blog.module.scss';
import { useClientSideNavigation } from '~/hooks/useClientSideNavigation';
import Head from 'next/head';
import { CMSBlogArticle, type CMSBlogImageRendition } from '~/networking/api/cms/responses/Blog';
import { getCMSBlog } from '~/networking/api/cms/requests/getCMSBlog';
import { PosthogHelper } from '~/helpers/PosthogHelper';
import { useGoogleTagManager } from '~/hooks/useGoogleTagManager';
import { PageTitleContext } from '~/hooks/useCustomPageTitle';
import { BackLink } from '~/components/modules/Breadcrumb/BackLink';
import { baseRoutes } from '~/routes/base';
import VWO from '~/components/global/document/head/VWO';

interface CMSBlogArticlePageProps {
    page: CMSBlogArticle;
}

export const CMSBlogArticlePage = ({ page }: CMSBlogArticlePageProps) => {
    const { googleTagManager } = useGoogleTagManager();
    const onClick = useClientSideNavigation();
    const onetime = useRef(false);

    useEffect(() => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://a.omappapi.com/app/js/api.min.js';
        script.async = true;
        script.dataset.user = '76468';
        script.dataset.account = '85986';

        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
    }, []);

    useEffect(() => {
        // for testing purposes
        void PosthogHelper.init().then((posthog) => {
            window.Vino.posthog = posthog;
        });

        if (!googleTagManager) return;

        PosthogHelper.onFeatureFlag(() => {
            if (PosthogHelper.getFeatureFlag('blog-captain') === 'test' && !onetime.current) {
                void googleTagManager.dispatchEvent('blog-captain');
                onetime.current = true;
            }
        });
    }, [googleTagManager]);

    let largestSize: CMSBlogImageRendition = { url: '', width: 0, height: 0 };
    const preloadSizes = Object.values(page.hero_image).map((value) => {
        if (value.width > largestSize.width) {
            largestSize = value;
        }
        return `${value.url} ${value.width}w`;
    });

    // the first instance should be the hero
    const body = page.body.replace('loading="lazy"', 'loading="eager"');

    return (
        <PageTitleContext.Provider value={{ pageTitle: page.title, backTo: '/blog' }}>
            <AppContainer>
                <Head>
                    <title>{page.title}</title>
                    <meta name={'description'} content={page.meta_preview_text} />
                    <link rel={'canonical'} href={`https://www.vinovest.co/blog/${page.slug}`} />
                    <link
                        rel={'preload'}
                        as={'image'}
                        href={largestSize.url}
                        imageSrcSet={preloadSizes.join(', ')}
                        imageSizes={'(max-width:1024px) 93vw, 1028px'}
                    />
                    <VWO />
                </Head>
                <Container classnames={'sm:max-w-[93%] lg:max-w-[1135px]'} maxWidth={'max-w-full md:container'}>
                    <div className={'md:mt-20 w-full mb-32'}>
                        <BackLink
                            maxWidth={'max-w-[900px]'}
                            classnames={'mb-[20px] mt-[20px] md:mt-[-40px]'}
                            previousRoute={baseRoutes.dashboard.href}
                        />
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                        <div
                            onClick={onClick}
                            className={styles.container}
                            dangerouslySetInnerHTML={{ __html: body }}
                        />
                    </div>
                </Container>
            </AppContainer>
        </PageTitleContext.Provider>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
    if (params?.slug === undefined || typeof params.slug !== 'string') {
        return { notFound: true };
    }

    const response = await getCMSBlog(params.slug);
    if (response.status !== 200) {
        if (response.status !== 404) {
            console.error('Failed to fetch Blog page', response);
        }
        return { notFound: true };
    }

    return {
        props: {
            page: response.data
        }
    };
};

export default CMSBlogArticlePage;
